/**
 * Software rasterizer behind BitmapData.draw(): Bitmaps/BitmapData, timeline shapes (rasterized
 * ImageRef pixels from the asset image store), morph samples, containers and button states, with
 * matrices, colour transforms (Flash's draw ignores the source object's own transform) and a clip
 * rectangle. Normal blending only.
 *
 * UNVERIFIED vs Flash: anti-aliasing/sampling details, blend modes other than normal, filters,
 * masks/scrollRect clipping, vector Graphics and TextField glyphs are not rasterized.
 */
import type { BitmapData } from './BitmapData';

type CT = [number, number, number, number, number, number, number, number] | null;

interface PxMatrix {
  a: number;
  b: number;
  c: number;
  d: number;
  tx: number;
  ty: number;
}

interface DrawCtx {
  target: BitmapData;
  clip: [number, number, number, number];
  touched: [number, number, number, number] | null;
  smoothing: boolean;
}

function mul(m1: PxMatrix, m2: PxMatrix): PxMatrix {
  return {
    a: m1.a * m2.a + m1.c * m2.b,
    b: m1.b * m2.a + m1.d * m2.b,
    c: m1.a * m2.c + m1.c * m2.d,
    d: m1.b * m2.c + m1.d * m2.d,
    tx: m1.a * m2.tx + m1.c * m2.ty + m1.tx,
    ty: m1.b * m2.tx + m1.d * m2.ty + m1.ty,
  };
}

function concatCT(outer: CT, inner: CT): CT {
  if (!outer) return inner;
  if (!inner) return outer;
  // result(c) = outer(inner(c))
  return [
    outer[0] * inner[0],
    outer[1] * inner[1],
    outer[2] * inner[2],
    outer[3] * inner[3],
    outer[0] * inner[4] + outer[4],
    outer[1] * inner[5] + outer[5],
    outer[2] * inner[6] + outer[6],
    outer[3] * inner[7] + outer[7],
  ];
}

function isIdentityCT(ct: CT): boolean {
  return !ct || (ct[0] === 1 && ct[1] === 1 && ct[2] === 1 && ct[3] === 1 && ct[4] === 0 && ct[5] === 0 && ct[6] === 0 && ct[7] === 0);
}

function premul(a: number, r: number, g: number, b: number): number {
  if (a >= 255) return ((255 << 24) | (r << 16) | (g << 8) | b) >>> 0;
  if (a <= 0) return 0;
  return ((a << 24) | ((((r * a + 127) / 255) | 0) << 16) | ((((g * a + 127) / 255) | 0) << 8) | (((b * a + 127) / 255) | 0)) >>> 0;
}

function clamp(v: number): number {
  return v < 0 ? 0 : v > 255 ? 255 : v | 0;
}

function blendOverRaw(dest: number, src: number): number {
  const sa = src >>> 24;
  if (sa === 255) return src >>> 0;
  if (sa === 0) return dest >>> 0;
  const inv = 256 - sa;
  const r = (((src >>> 16) & 255) + ((((dest >>> 16) & 255) * inv) >> 8)) & 255;
  const g = (((src >>> 8) & 255) + ((((dest >>> 8) & 255) * inv) >> 8)) & 255;
  const b = ((src & 255) + (((dest & 255) * inv) >> 8)) & 255;
  const a = (sa + (((dest >>> 24) * inv) >> 8)) & 255;
  return ((a << 24) | (r << 16) | (g << 8) | b) >>> 0;
}

/** Pixel source abstraction: returns premultiplied ARGB at integer coords (in range). */
interface PixelSource {
  width: number;
  height: number;
  get(x: number, y: number): number;
}

function sourceFromBitmapData(bd: BitmapData): PixelSource {
  const d = bd.$data;
  const w = bd.$width;
  return { width: w, height: bd.$height, get: (x, y) => d[y * w + x] };
}

function sourceFromRGBA(img: { width: number; height: number; data: Uint8ClampedArray | Uint8Array }): PixelSource {
  const d = img.data;
  const w = img.width;
  return {
    width: w,
    height: img.height,
    get: (x, y) => {
      const i = (y * w + x) * 4;
      return premul(d[i + 3], d[i], d[i + 1], d[i + 2]);
    },
  };
}

function drawPixels(ctx: DrawCtx, src: PixelSource, sx: number, sy: number, sw: number, sh: number, m: PxMatrix, ct: CT, smooth: boolean): void {
  const det = m.a * m.d - m.b * m.c;
  if (!det || !Number.isFinite(det)) return;
  // Destination bounding box of the source rectangle.
  const xs = [0, sw, 0, sw];
  const ys = [0, 0, sh, sh];
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (let i = 0; i < 4; i++) {
    const X = m.a * xs[i] + m.c * ys[i] + m.tx;
    const Y = m.b * xs[i] + m.d * ys[i] + m.ty;
    minX = Math.min(minX, X);
    minY = Math.min(minY, Y);
    maxX = Math.max(maxX, X);
    maxY = Math.max(maxY, Y);
  }
  const [cx0, cy0, cx1, cy1] = ctx.clip;
  const x0 = Math.max(cx0, Math.floor(minX));
  const y0 = Math.max(cy0, Math.floor(minY));
  const x1 = Math.min(cx1, Math.ceil(maxX));
  const y1 = Math.min(cy1, Math.ceil(maxY));
  if (x1 <= x0 || y1 <= y0) return;
  const ia = m.d / det;
  const ib = -m.b / det;
  const ic = -m.c / det;
  const id = m.a / det;
  const target = ctx.target;
  const W = target.$width;
  const data = target.$data;
  const opaque = !target.$transparent;
  const hasCT = !isIdentityCT(ct);
  let touchedAny = false;
  let tx0 = Infinity;
  let ty0 = Infinity;
  let tx1 = -Infinity;
  let ty1 = -Infinity;
  for (let y = y0; y < y1; y++) {
    for (let x = x0; x < x1; x++) {
      const px = x + 0.5 - m.tx;
      const py = y + 0.5 - m.ty;
      const u = ia * px + ic * py;
      const v = ib * px + id * py;
      let c: number;
      if (smooth) {
        const fu = u - 0.5;
        const fv = v - 0.5;
        const iu = Math.floor(fu);
        const iv = Math.floor(fv);
        if (fu < -0.5 || fv < -0.5 || fu > sw - 0.5 || fv > sh - 0.5) continue;
        const du = fu - iu;
        const dv = fv - iv;
        const s = (qx: number, qy: number) => {
          const cxq = Math.min(sw - 1, Math.max(0, qx));
          const cyq = Math.min(sh - 1, Math.max(0, qy));
          return src.get(sx + cxq, sy + cyq);
        };
        const p00 = s(iu, iv);
        const p10 = s(iu + 1, iv);
        const p01 = s(iu, iv + 1);
        const p11 = s(iu + 1, iv + 1);
        const w00 = (1 - du) * (1 - dv);
        const w10 = du * (1 - dv);
        const w01 = (1 - du) * dv;
        const w11 = du * dv;
        const ch = (sh8: number) =>
          Math.round(((p00 >>> sh8) & 255) * w00 + ((p10 >>> sh8) & 255) * w10 + ((p01 >>> sh8) & 255) * w01 + ((p11 >>> sh8) & 255) * w11);
        c = ((ch(24) << 24) | (ch(16) << 16) | (ch(8) << 8) | ch(0)) >>> 0;
      } else {
        const iu = Math.floor(u);
        const iv = Math.floor(v);
        if (iu < 0 || iv < 0 || iu >= sw || iv >= sh) continue;
        c = src.get(sx + iu, sy + iv);
      }
      if (hasCT && ct) {
        const a = c >>> 24;
        if (a === 0) continue;
        // unpremultiply (float), transform, re-premultiply
        const r = (((c >>> 16) & 255) * 255) / a;
        const g = (((c >>> 8) & 255) * 255) / a;
        const b = ((c & 255) * 255) / a;
        c = premul(clamp(a * ct[3] + ct[7]), clamp(r * ct[0] + ct[4]), clamp(g * ct[1] + ct[5]), clamp(b * ct[2] + ct[6]));
      }
      if ((c >>> 24) === 0) continue;
      const di = y * W + x;
      let out = blendOverRaw(data[di], c);
      if (opaque) out = (out | 0xff000000) >>> 0;
      data[di] = out;
      touchedAny = true;
      if (x < tx0) tx0 = x;
      if (y < ty0) ty0 = y;
      if (x > tx1) tx1 = x;
      if (y > ty1) ty1 = y;
    }
  }
  if (touchedAny) {
    const t = ctx.touched;
    if (!t) ctx.touched = [tx0, ty0, tx1 + 1, ty1 + 1];
    else {
      t[0] = Math.min(t[0], tx0);
      t[1] = Math.min(t[1], ty0);
      t[2] = Math.max(t[2], tx1 + 1);
      t[3] = Math.max(t[3], ty1 + 1);
    }
  }
}

function objMatrix(o: any): PxMatrix {
  const m = o.$m;
  return { a: m.a, b: m.b, c: m.c, d: m.d, tx: m.tx / 20, ty: m.ty / 20 };
}

function objCT(o: any): CT {
  const ct: CT = [o.$ctRM / 256, o.$ctGM / 256, o.$ctBM / 256, o.$ctAM / 256, o.$ctRO, o.$ctGO, o.$ctBO, o.$ctAO];
  return isIdentityCT(ct) ? null : ct;
}

function drawImageRef(ctx: DrawCtx, img: any, ref: any, m: PxMatrix, ct: CT): void {
  if (!img || !ref) return;
  const scale = ref.scale || 1;
  const sx = ref.sx ?? 0;
  const sy = ref.sy ?? 0;
  const sw = ref.sw ?? img.width;
  const sh = ref.sh ?? img.height;
  const local: PxMatrix = { a: 1 / scale, b: 0, c: 0, d: 1 / scale, tx: ref.offsetX, ty: ref.offsetY };
  drawPixels(ctx, sourceFromRGBA(img), sx, sy, sw, sh, mul(m, local), ct, true);
}

function drawObject(ctx: DrawCtx, o: any, m: PxMatrix, ct: CT, isRoot: boolean): void {
  if (!isRoot) {
    if (!o.$visible || o.$maskee) return;
    m = mul(m, objMatrix(o));
    ct = concatCT(ct, objCT(o));
  }
  switch (o.$renderKind) {
    case 'bitmap': {
      const bd = o.$bitmapData;
      if (bd && !bd.$disposed) drawPixels(ctx, sourceFromBitmapData(bd), 0, 0, bd.$width, bd.$height, m, ct, ctx.smoothing || !!o.$smoothing);
      break;
    }
    case 'shape':
    case 'morph': {
      const r = o.$imageForRender?.();
      if (r) drawImageRef(ctx, r.image, r.ref, m, ct);
      break;
    }
    case 'button': {
      const s = o.$currentStateObject?.();
      if (s) drawObject(ctx, s, m, ct, false);
      break;
    }
    default:
      break;
  }
  const kids = o.$isContainer ? o.$children : null;
  if (kids) for (const k of kids) drawObject(ctx, k, m, ct, false);
}

/** Entry point for BitmapData.draw. Returns the touched region or null. */
export function drawSourceInto(
  target: BitmapData,
  source: any,
  matrix: { a: number; b: number; c: number; d: number; tx: number; ty: number } | null,
  colorTransform: any,
  _blendMode: string | null,
  clipRect: { x: number; y: number; width: number; height: number } | null,
  smoothing: boolean,
): [number, number, number, number] | null {
  const W = target.$width;
  const H = target.$height;
  let clip: [number, number, number, number] = [0, 0, W, H];
  if (clipRect) {
    const x0 = Math.max(0, Math.floor(clipRect.x));
    const y0 = Math.max(0, Math.floor(clipRect.y));
    const x1 = Math.min(W, Math.ceil(clipRect.x + clipRect.width));
    const y1 = Math.min(H, Math.ceil(clipRect.y + clipRect.height));
    if (x1 <= x0 || y1 <= y0) return null;
    clip = [x0, y0, x1, y1];
  }
  const m: PxMatrix = matrix ? { a: matrix.a, b: matrix.b, c: matrix.c, d: matrix.d, tx: matrix.tx, ty: matrix.ty } : { a: 1, b: 0, c: 0, d: 1, tx: 0, ty: 0 };
  const ct: CT = colorTransform
    ? [
        colorTransform.redMultiplier,
        colorTransform.greenMultiplier,
        colorTransform.blueMultiplier,
        colorTransform.alphaMultiplier,
        colorTransform.redOffset,
        colorTransform.greenOffset,
        colorTransform.blueOffset,
        colorTransform.alphaOffset,
      ]
    : null;
  const ctx: DrawCtx = { target, clip, touched: null, smoothing: !!smoothing };
  if (source && source.$data instanceof Uint32Array && typeof source.$width === 'number') {
    if (source.$disposed) throw new Error('ArgumentError: Error #2015: Invalid BitmapData.');
    drawPixels(ctx, sourceFromBitmapData(source), 0, 0, source.$width, source.$height, m, ct, !!smoothing);
  } else if (source && typeof source.$renderKind === 'string') {
    drawObject(ctx, source, m, ct, true);
  } else {
    throw new TypeError('Error #1034: Type Coercion failed: source is not an IBitmapDrawable.');
  }
  return ctx.touched;
}
